import { useCallback, useState } from 'react';
import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters from 'hooks/useQueryParameters';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';
import { setExpireTime } from 'state/otpCode';
import { useDispatch, useSelector } from 'react-redux';
import { setMagicLink } from 'state/magicLink';
import { selectTimeouts } from 'state/timeouts';

export interface MagicLinkAuthResult {
  magicLinkId: string;
  status: 'AUTH_STARTED' | 'LINK_ACCEPTED';
}

export class MagicLinkAuthError extends Error {}

export type MagicLinkAuthParams = {
  zip: string;
  mobilePhone: string;
} & ({ ssn: string } | { emailAddress: string });

type MagicLinkAuthHook = () => [
  (params: MagicLinkAuthParams) => Promise<MagicLinkAuthResult>,
  Status,
  boolean,
];

const PATH = '/api/magic_link_auth';

const useMagicLinkAuth: MagicLinkAuthHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { magicLinkStatusTimeout } = useSelector(selectTimeouts);
  const { mockMagicLinkAuth } = useQueryParameters();
  const overrideAttributes = useApplicationBasedOverrides();
  const dispatch = useDispatch();

  const magicLinkAuthRequest = useCallback(
    async (params: MagicLinkAuthParams) => {
      setError(false);
      const response = await fetch(PATH, {
        method: 'POST',
        body: {
          ...params,
          ...overrideAttributes,
          mockResponse: mockMagicLinkAuth,
        },
      });

      if (!response.ok) {
        setError(true);
        return Promise.reject(new MagicLinkAuthError());
      }

      const result: MagicLinkAuthResult = await response.json();

      dispatch(setExpireTime(magicLinkStatusTimeout));
      dispatch(setMagicLink(result));

      return Promise.resolve(result);
    },
    [
      dispatch,
      fetch,
      magicLinkStatusTimeout,
      mockMagicLinkAuth,
      overrideAttributes,
    ],
  );

  return [magicLinkAuthRequest, status, error];
};

export default useMagicLinkAuth;
